(() => {
  if (customElements.get('om-designer-menu')) return;
  const WEEK = 604800000;
  const instances = new Set();
  let clockBase = null;
  let clockAt = 0;
  let wallAt = 0;
  let retryClock;
  let pendingClock = null;
  let lastAttempt = -Infinity;
  const now = () => clockBase === null ? Date.now() : clockBase + Math.max(performance.now() - clockAt, Date.now() - wallAt);
  const weekAt = (time, epoch) => Math.max(0, Math.floor((time - epoch) / WEEK));
  function syncClock(force = false) {
    if (pendingClock) return pendingClock;
    if (!force && performance.now() - lastAttempt < 60000) return Promise.resolve(true);
    lastAttempt = performance.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    pendingClock = fetch(window.location.href, {
      method: 'HEAD', cache: 'no-store', credentials: 'same-origin', signal: controller.signal
    }).then(response => {
      if (!response.ok) return false;
      const date = Date.parse(response.headers.get('Date') || '');
      const rawAge = Number(response.headers.get('Age') || 0);
      const age = Number.isFinite(rawAge) && rawAge >= 0 ? rawAge * 1000 : 0;
      if (!Number.isFinite(date)) return false;
      clockBase = date + age;
      clockAt = performance.now();
      wallAt = Date.now();
      instances.forEach(instance => instance.update());
      return true;
    }).catch(() => false).finally(() => {
      clearTimeout(timeout);
      pendingClock = null;
    });
    return pendingClock;
  }
  class OmDesignerMenu extends HTMLElement {
    connectedCallback() {
      this.epoch = Number(this.dataset.epoch) * 1000;
      this.featured = this.querySelector('[data-om-designer-featured]');
      const handles = new Set();
      this.candidates = Array.from(this.querySelectorAll('[data-om-designer-candidate]')).filter(anchor => {
        const handle = anchor.dataset.omDesignerCandidate;
        if (!handle || handles.has(handle)) return false;
        handles.add(handle);
        return true;
      });
      if (!this.featured || !Number.isFinite(this.epoch)) return;
      instances.add(this);
      this.update();
      syncClock();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      instances.delete(this);
      if (!instances.size) clearTimeout(retryClock);
    }
    update() {
      clearTimeout(this.timer);
      if (!this.isConnected) return;
      const time = now();
      const week = weekAt(time, this.epoch);
      const count = this.candidates.length;
      const selected = [];
      for (let slot = 0; slot < Math.min(3, count); slot += 1) {
        selected.push(this.candidates[(week * 3 + slot) % count]);
      }
      const nextKey = selected.map(anchor => anchor.dataset.omDesignerCandidate).join(',');
      const currentKey = Array.from(this.featured.querySelectorAll('[data-om-featured-handle]')).map(anchor => anchor.dataset.omFeaturedHandle).join(',');
      if (nextKey !== currentKey) {
        const currentLinks = Array.from(this.featured.querySelectorAll('a'));
        const focusIndex = currentLinks.indexOf(document.activeElement);
        const items = selected.map(anchor => {
          const item = document.createElement('li');
          const copy = anchor.cloneNode(true);
          copy.dataset.omFeaturedHandle = anchor.dataset.omDesignerCandidate;
          delete copy.dataset.omDesignerCandidate;
          item.append(copy);
          return item;
        });
        this.featured.replaceChildren(...items);
        if (focusIndex >= 0) this.featured.querySelectorAll('a')[Math.min(focusIndex, selected.length - 1)]?.focus({preventScroll: true});
      }
      this.dataset.week = String(week);
      const nextBoundary = this.epoch + (week + 1) * WEEK;
      this.timer = setTimeout(() => {
        this.update();
        syncClock();
      }, Math.max(50, Math.min(WEEK, nextBoundary - time + 50)));
    }
  }
  customElements.define('om-designer-menu', OmDesignerMenu);
  const refresh = () => {
    if (document.visibilityState === 'hidden') return;
    instances.forEach(instance => instance.update());
    syncClock(true).then(ok => {
      if (!ok && instances.size && document.visibilityState !== 'hidden') {
        clearTimeout(retryClock);
        retryClock = setTimeout(() => syncClock(true), 60000);
      }
    });
  };
  document.addEventListener('visibilitychange', refresh);
  window.addEventListener('pageshow', refresh);
})();
